// src/AdminDashboard.tsx
import React, { useState } from 'react'
import { Button, Space, Tabs, Typography } from 'antd'
import AnalyticsTab from './Admin/AnalyticsTab'
import AICostsTab from './Admin/AICostsTab'
import RevenueHealthTab from './Admin/RevenueHealthTab'
import TranslationReliabilityTab from './Admin/TranslationReliabilityTab'
import ManagementTab from './Admin/AdminManagementTab'
import AdminBillingTab from './Admin/AdminBillingTab'
import AITestingTab from './Admin/AITestingTab'
import AIModelManager from './Admin/AIModelManager'
import LanguageSurveyAdmin from './Admin/LanguageSurveyAdmin'
import GiftEmailAdmin from './Admin/GiftEmailAdmin'
import YearEndEmailAdmin from './Admin/YearEndEmailAdmin'
import AdminGlossaryBackfillTab from './Admin/AdminGlossaryBackfillTab'
import AdminFrenchDialogueLegacyTab from './Admin/AdminFrenchDialogueLegacyTab'
import AdminStageTraceCostExportTab from './Admin/AdminStageTraceCostExportTab'
import AdminTutorialsTab from './Admin/AdminTutorialsTab'
import AdminAnnouncements from './Admin/AdminAnnouncements'
import AdminCommunications from './Admin/AdminCommunications'
import AdminPromoCodes from './Admin/AdminPromoCodes'
import SupportIntakePrerequisites from './Admin/SupportIntakePrerequisites'
import type { UsersSharedState } from './HeaderComponent'
import './AdminDashboard.css'

const { Title, Text } = Typography

const ADMIN_PRETRANSLATION_MODEL_DEFAULT = 'Gemini2.5Pro'
const ADMIN_TEST_PROOFREAD_MODEL_1_DEFAULT = ''
const ADMIN_TEST_PROOFREAD_MODEL_2_DEFAULT = ''
const ADMIN_TEST_PROOFREAD_MODEL_3_DEFAULT = ''
const ADMIN_TRANSLATION_TENSE_GUARD_MODEL_DEFAULT = ''
const ADMIN_PROOFREAD_CHANGE_AUDIT_MODEL_DEFAULT = ''

interface AdminDashboardProps {
  isAuth: boolean
  usersSharedState: UsersSharedState
  setUsersSharedState: React.Dispatch<React.SetStateAction<UsersSharedState>>
  showLoginModal: () => void
  mode?: 'admin' | 'support'
}

type WorkflowKey =
  | 'support'
  | 'revenue'
  | 'ai-ops'
  | 'content-ops'
  | 'communications'
  | 'system-maintenance'

type RevenueTabKey = 'overview' | 'revenue-health' | 'analytics' | 'ai-costs' | 'billing' | 'promo-codes'

type WorkflowDefinition = {
  key: WorkflowKey
  label: string
  description: string
  roles: Array<'admin' | 'support'>
  render: () => React.ReactNode
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  isAuth,
  usersSharedState,
  mode = 'admin',
}) => {
  const hasRole = (role: 'admin' | 'support') =>
    Array.isArray(usersSharedState.roles) && usersSharedState.roles.includes(role)
  const isAdmin = hasRole('admin')
  const isSupport = hasRole('support')
  const effectiveMode = mode === 'support' ? 'support' : 'admin'
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowKey>('support')
  const [activeRevenueTab, setActiveRevenueTab] = useState<RevenueTabKey>('overview')

  const managementDefaults = {
    usersSharedState,
    forcedPretranslationModelId: ADMIN_PRETRANSLATION_MODEL_DEFAULT,
    forcedTestProofreadModel1Id: ADMIN_TEST_PROOFREAD_MODEL_1_DEFAULT,
    forcedTestProofreadModel2Id: ADMIN_TEST_PROOFREAD_MODEL_2_DEFAULT,
    forcedTestProofreadModel3Id: ADMIN_TEST_PROOFREAD_MODEL_3_DEFAULT,
    forcedTranslationTenseGuardModelId: ADMIN_TRANSLATION_TENSE_GUARD_MODEL_DEFAULT,
    forcedProofreadChangeAuditModelId: ADMIN_PROOFREAD_CHANGE_AUDIT_MODEL_DEFAULT,
  }

  const supportWorkflow = () => (
    <Tabs
      items={[
        ...(isAdmin && effectiveMode === 'admin' ? [{
          key: 'intake-prerequisites',
          label: 'Intake prerequisites',
          children: <SupportIntakePrerequisites key={usersSharedState.userID} />,
        }] : []),
        {
          key: 'user-remediation',
          label: 'User Remediation',
          children: (
            <ManagementTab
              {...managementDefaults}
              mode="support"
              allowedTabs={['user']}
              defaultTab="user"
              hideTabsWhenSingle
            />
          ),
        },
        {
          key: 'book-support',
          label: 'Book Search & Copy',
          children: (
            <ManagementTab
              {...managementDefaults}
              mode="support"
              allowedTabs={['content']}
              defaultTab="content"
              hideTabsWhenSingle
            />
          ),
        },
        {
          key: 'french-dialogue-reset',
          label: 'French Dialogue Reset',
          children: <AdminFrenchDialogueLegacyTab />,
        },
        {
          key: 'lock-support',
          label: 'Lock & Support Codes',
          children: (
            <ManagementTab
              {...managementDefaults}
              mode="support"
              allowedTabs={['operations']}
              defaultTab="operations"
              hideTabsWhenSingle
              operationPanels={{
                adminTools: false,
                lockSupport: true,
                lockInspector: false,
                manifestInspector: false,
                aiModelRoles: false,
              }}
            />
          ),
        },
      ]}
    />
  )

  const renderRevenueOverview = () => (
    <div className="admin-dashboard-revenue-overview">
      <div className="admin-dashboard-revenue-overview__header">
        <div>
          <Text type="secondary">Revenue workspace</Text>
          <Title level={3} style={{ marginBottom: 0 }}>
            Choose the revenue view for the question you are answering.
          </Title>
        </div>
      </div>
      <div className="admin-dashboard-revenue-overview__grid">
        <button type="button" onClick={() => setActiveRevenueTab('revenue-health')}>
          <span>Health</span>
          <strong>MRR, churn, retention, conversion, and plan health.</strong>
        </button>
        <button type="button" onClick={() => setActiveRevenueTab('analytics')}>
          <span>Analytics</span>
          <strong>Usage, activity, and operational metrics.</strong>
        </button>
        <button type="button" onClick={() => setActiveRevenueTab('ai-costs')}>
          <span>AI Costs</span>
          <strong>Provider spend, margins, drilldowns, and forecasts.</strong>
        </button>
        <button type="button" onClick={() => setActiveRevenueTab('billing')}>
          <span>Billing</span>
          <strong>Plan configuration and account billing tools.</strong>
        </button>
        <button type="button" onClick={() => setActiveRevenueTab('promo-codes')}>
          <span>Promo Codes</span>
          <strong>Single and bulk forever-credit campaigns.</strong>
        </button>
      </div>
    </div>
  )

  const adminModeForTools = 'admin' as const
  const workflowDefinitions: WorkflowDefinition[] = [
      {
        key: 'support',
        label: 'Support',
        description: 'Customer remediation, book copy tools, support codes, and common reset flows.',
        roles: ['support'],
        render: supportWorkflow,
      },
      {
        key: 'revenue',
        label: 'Revenue',
        description: 'Revenue health, usage analytics, billing plans, and promo campaigns.',
        roles: ['admin'],
        render: () => (
          <Tabs
            activeKey={activeRevenueTab}
            onChange={(key) => setActiveRevenueTab(key as RevenueTabKey)}
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: renderRevenueOverview(),
              },
              {
                key: 'revenue-health',
                label: 'Revenue Health',
                children: activeRevenueTab === 'revenue-health' ? <RevenueHealthTab isAdmin={isAdmin} /> : null,
              },
              {
                key: 'analytics',
                label: 'Analytics',
                children: activeRevenueTab === 'analytics' ? <AnalyticsTab isAdmin={isAdmin} /> : null,
              },
              {
                key: 'ai-costs',
                label: 'AI Costs',
                children: activeRevenueTab === 'ai-costs' ? <AICostsTab isAdmin={isAdmin} /> : null,
              },
              {
                key: 'billing',
                label: 'Billing Plans',
                children: activeRevenueTab === 'billing' ? (
                  <AdminBillingTab usersSharedState={usersSharedState} />
                ) : null,
              },
              {
                key: 'promo-codes',
                label: 'Promo Codes',
                children: activeRevenueTab === 'promo-codes' ? <AdminPromoCodes /> : null,
              },
            ]}
          />
        ),
      },
      {
        key: 'ai-ops',
        label: 'AI Ops',
        description: 'Model registry, role assignments, AI testing, reliability, and cost drilldowns.',
        roles: ['admin'],
        render: () => (
          <Tabs
            items={[
              { key: 'ai-models', label: 'AI Models', children: <AIModelManager /> },
              {
                key: 'model-roles',
                label: 'Model Role Assignments',
                children: (
                  <ManagementTab
                    {...managementDefaults}
                    mode={adminModeForTools}
                    allowedTabs={['operations']}
                    defaultTab="operations"
                    hideTabsWhenSingle
                    operationPanels={{
                      adminTools: false,
                      lockSupport: false,
                      lockInspector: false,
                      manifestInspector: false,
                      aiModelRoles: true,
                    }}
                  />
                ),
              },
              { key: 'ai-testing', label: 'AI Testing', children: <AITestingTab /> },
              { key: 'translation-reliability', label: 'Translation Reliability', children: <TranslationReliabilityTab /> },
              { key: 'stage-trace-costs', label: 'Stage Trace Costs', children: <AdminStageTraceCostExportTab /> },
              {
                key: 'ai-logs',
                label: 'AI Logs',
                children: (
                  <ManagementTab
                    {...managementDefaults}
                    mode={adminModeForTools}
                    allowedTabs={['ai-logs']}
                    defaultTab="ai-logs"
                    hideTabsWhenSingle
                  />
                ),
              },
            ]}
          />
        ),
      },
      {
        key: 'content-ops',
        label: 'Content Ops',
        description: 'Book/content maintenance, language surveys, glossary backfills, and legacy content repair.',
        roles: ['admin'],
        render: () => (
          <Tabs
            items={[
              {
                key: 'book-production',
                label: 'Book Production',
                children: (
                  <div className="admin-dashboard-surface">
                    <Title level={3}>Autonomous Book Production</Title>
                    <Text type="secondary">
                      Define the creative intent, approve one Creative Lock, and monitor AI drafting, quality repair,
                      exceptions, and release packages.
                    </Text>
                    <div style={{ marginTop: 16 }}>
                      <Button type="primary" href="/admin/create-book">
                        Open Book Production
                      </Button>
                    </div>
                  </div>
                ),
              },
              {
                key: 'content-tools',
                label: 'Content Tools',
                children: (
                  <ManagementTab
                    {...managementDefaults}
                    mode={adminModeForTools}
                    allowedTabs={['content']}
                    defaultTab="content"
                    hideTabsWhenSingle
                  />
                ),
              },
              { key: 'french-dialogue', label: 'French Dialogue Reset', children: <AdminFrenchDialogueLegacyTab /> },
              { key: 'glossary-backfill', label: 'Glossary Backfill', children: <AdminGlossaryBackfillTab /> },
              { key: 'language-surveys', label: 'Language Surveys', children: <LanguageSurveyAdmin /> },
              { key: 'tutorials', label: 'Tutorials', children: <AdminTutorialsTab /> },
            ]}
          />
        ),
      },
      {
        key: 'communications',
        label: 'Communications',
        description: 'In-app announcements and transactional email templates.',
        roles: ['admin'],
        render: () => (
          <Tabs
            items={[
              { key: 'announcements', label: 'Announcements', children: <AdminAnnouncements /> },
              { key: 'confirmed-emails', label: 'Send Emails', children: <AdminCommunications /> },
              { key: 'gift-emails', label: 'Gift Emails', children: <GiftEmailAdmin /> },
              { key: 'year-end-emails', label: 'Year-End Emails', children: <YearEndEmailAdmin /> },
            ]}
          />
        ),
      },
      {
        key: 'system-maintenance',
        label: 'System Maintenance',
        description: 'Bulk repairs, lock/manifest inspection, cache resets, queues, and backfills.',
        roles: ['admin'],
        render: () => (
          <Tabs
            items={[
              {
                key: 'locks-manifests',
                label: 'Locks & Manifests',
                children: (
                  <ManagementTab
                    {...managementDefaults}
                    mode={adminModeForTools}
                    allowedTabs={['operations']}
                    defaultTab="operations"
                    hideTabsWhenSingle
                    operationPanels={{
                      adminTools: true,
                      lockSupport: true,
                      lockInspector: true,
                      manifestInspector: true,
                      aiModelRoles: false,
                    }}
                  />
                ),
              },
              {
                key: 'queues-backfills',
                label: 'Queues & Backfills',
                children: (
                  <ManagementTab
                    {...managementDefaults}
                    mode={adminModeForTools}
                    allowedTabs={['monitoring']}
                    defaultTab="monitoring"
                    hideTabsWhenSingle
                  />
                ),
              },
            ]}
          />
        ),
      },
    ]

  const availableWorkflows = workflowDefinitions.filter((workflow) =>
    workflow.roles.some((role) => {
      if (effectiveMode === 'support') {
        return role === 'support' && (isSupport || isAdmin)
      }
      return role === 'admin' ? isAdmin : isSupport || isAdmin
    })
  )

  React.useEffect(() => {
    if (!availableWorkflows.find((workflow) => workflow.key === activeWorkflow)) {
      setActiveWorkflow(availableWorkflows[0]?.key ?? 'support')
    }
  }, [activeWorkflow, availableWorkflows])

  if (!isAuth) {
    return null
  }
  if (effectiveMode === 'support') {
    if (!isSupport && !isAdmin) {
      return null
    }
  } else if (!isAdmin) {
    return null
  }

  return (
    <main className="admin-dashboard-page">
      <div className="admin-dashboard-shell">
        <section className="admin-dashboard-header" aria-labelledby="admin-dashboard-title">
          <div>
            <div className="admin-dashboard-kicker">
              {effectiveMode === 'support' ? 'Support mode' : 'Admin mode'}
            </div>
            <Title level={2} className="admin-dashboard-title" id="admin-dashboard-title">
              {effectiveMode === 'support' ? 'Support Dashboard' : 'Admin Dashboard'}
            </Title>
            <Text type="secondary">
              {availableWorkflows.find((workflow) => workflow.key === activeWorkflow)?.description}
            </Text>
          </div>
          <div className="admin-dashboard-header__side">
            <div className="admin-dashboard-status" aria-label="Access status">
              <span>{effectiveMode === 'support' ? 'Support access' : 'Admin access'}</span>
              <span>{availableWorkflows.length} workflows</span>
            </div>
            {effectiveMode !== 'support' && (
              <Space className="admin-dashboard-quick-actions" wrap>
                <Button
                  size="small"
                  onClick={() => {
                    setActiveWorkflow('revenue')
                    setActiveRevenueTab('revenue-health')
                  }}
                >
                  Revenue Health
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setActiveWorkflow('ai-ops')
                  }}
                >
                  AI Testing
                </Button>
              </Space>
            )}
          </div>
        </section>

        <section className="admin-dashboard-workspace" aria-label="Admin workflows">
          <Tabs
            activeKey={activeWorkflow}
            onChange={(key) => {
              if (key === 'revenue') {
                setActiveRevenueTab('overview')
              }
              setActiveWorkflow(key as WorkflowKey)
            }}
            items={availableWorkflows.map((workflow) => ({
              key: workflow.key,
              label: workflow.label,
              children: activeWorkflow === workflow.key ? workflow.render() : null,
            }))}
          />
        </section>
      </div>
    </main>
  )
}

export default AdminDashboard
